import {
  Avatar as CoreAvatar,
  AvatarProps as CoreAvatarProps,
} from "./internal/Avatar";
import { forwardRef } from "react";
import { DistributiveOmit } from "../../types";
import { AvatarGroup } from "./AvatarGroup";

/**
 * Props for the Avatar component
 * @property {string} name - Full name used for alt text and screen reader
 * @property {string} [color] - Custom color of the Avatar
 * @property {string} [image] - Image source to be used for Avatar
 * @property {"small" | "medium" | "large"} [size] - Size of the Avatar
 * @property {"online" | "offline"} [status] - Online status of the Avatar
 * @extends ComponentPropsWithoutRef<"span">
 * @extends Omit<AvatarBaseProps, "initials">
 * @extends Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink">
 * @extends DataTrackingId
 */
export type AvatarProps = DistributiveOmit<CoreAvatarProps, "size"> & {
  size?: Extract<CoreAvatarProps["size"], "small" | "medium" | "large">;
};

/**
 * Avatar component for displaying user avatars with initials or images.
 *
 * Features:
 * - Displays user initials when no image is provided
 * - Supports custom colors and sizes
 * - Shows online/offline status
 * - Fully accessible with screen reader support
 * - Automatic image fallback handling
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Avatar name="John Doe" size="medium" status="online" />
 */
export const Avatar = Object.assign(
  forwardRef<HTMLSpanElement, AvatarProps>(function AvatarInner(props, ref) {
    return <CoreAvatar ref={ref} {...props} />;
  }),
  {
    /**
     * AvatarGroup component for displaying multiple avatars in a group.
     *
     * Features:
     * - Displays multiple avatars in a stacked layout
     * - Handles overflow with a count indicator
     * - Maintains consistent sizing across all avatars
     * - Supports custom styling and layout
     * - Automatically adjusts z-index for proper stacking
     * - Fully accessible with screen reader support
     *
     * @example
     * <Avatar.Group max={3}>
     *   <Avatar name="John Doe" />
     *   <Avatar name="Jane Smith" />
     *   <Avatar name="Bob Johnson" />
     *   <Avatar name="Alice Brown" />
     * </Avatar.Group>
     */
    Group: AvatarGroup,
  },
);
Avatar.displayName = CoreAvatar.displayName;
